import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import FormInput from "./FormInput";
import TermSettings from "./TermSettings";
//import Modal from './Modal'; // existing design system modal
import { userRequest } from "../../utils/requestMethods";
import { useStateContext } from "../../contexts/ContextProvider";
import { MobileFriendlyNotification, PhotoUploadModal } from "../../components";
import { DEFAULT_ARM_LABEL, getArmLabel } from "../../utils/classGroupHelpers";

const GeneralSettings = ({ currentColor }) => {
  const { school, refreshSchool } = useStateContext();
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [schoolData, setSchoolData] = useState(school);
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState(null);

  // Class Grouping & Shared Subjects settings state
  const [classGroupingEnabled, setClassGroupingEnabled] = useState(
    school?.settings?.class_grouping_enabled ?? false,
  );
  const [classGroupingSaving, setClassGroupingSaving] = useState(false);
  const [subclassLabelInput, setSubclassLabelInput] = useState(getArmLabel(school));
  const [subclassLabelSaving, setSubclassLabelSaving] = useState(false);

  useEffect(() => {
    setClassGroupingEnabled(school?.settings?.class_grouping_enabled ?? false);
    setSubclassLabelInput(getArmLabel(school));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school?.settings?.class_grouping_enabled, school?.settings?.subclass_label]);

  const handleClassGroupingToggle = async () => {
    const newValue = !classGroupingEnabled;
    setClassGroupingEnabled(newValue);
    setClassGroupingSaving(true);
    try {
      await userRequest.post(`/schools/${school?._id}/settings`, {
        class_grouping_enabled: newValue,
      });
      toast.success(`Class grouping ${newValue ? "enabled" : "disabled"}`);
      refreshSchool();
    } catch (err) {
      setClassGroupingEnabled(!newValue);
      toast.error("Failed to update class grouping setting");
    } finally {
      setClassGroupingSaving(false);
    }
  };

  const handleSaveSubclassLabel = async (e) => {
    e?.preventDefault();
    const cleanLabel = (subclassLabelInput || "").trim() || DEFAULT_ARM_LABEL;
    setSubclassLabelSaving(true);
    try {
      await userRequest.post(`/schools/${school?._id}/settings`, {
        subclass_label: cleanLabel,
      });
      toast.success(`Terminology label updated to "${cleanLabel}"`);
      refreshSchool();
    } catch (err) {
      toast.error("Failed to update terminology label");
    } finally {
      setSubclassLabelSaving(false);
    }
  };

  // Term state management
  const [termData, setTermData] = useState({});

  // Sync schoolData with school prop when it loads
  useEffect(() => {
    if (school) {
      setSchoolData(school);
      //console.log('GeneralSettings: School data synced', school);
    }
  }, [school]);

  useEffect(() => {
    if (school?.settings?.terms) {
      const termMap = {};
      school.settings.terms.forEach((term) => {
        termMap[term.term] = {
          term: term.term,
          start_date: term.start_date.split("T")[0],
          end_date: term.end_date.split("T")[0],
        };
      });
      setTermData(termMap);
    }
  }, [school?.settings?.terms]);

  const handleTermDateChange = (termNumber, field, value) => {
    setTermData((prev) => ({
      ...prev,
      [termNumber]: {
        ...prev[termNumber],
        term: termNumber,
        [field]: value,
      },
    }));
  };

  // Enhanced Geolocation state
  const [coordinates, setCoordinates] = useState({
    centerLat: "",
    centerLng: "",
    boundaryPoints: [],
  });
  const [accuracy, setAccuracy] = useState(null);
  const [isCollecting, setIsCollecting] = useState(false);
  const [collectionMode, setCollectionMode] = useState("center"); // 'center' or 'boundary'
  const [readings, setReadings] = useState([]);
  const [geoError, setGeoError] = useState("");
  const watchIdRef = useRef(null);

  useEffect(() => {
    if (schoolData?.location) {
      setCoordinates({
        centerLat: schoolData.location.latitude?.toString() || "",
        centerLng: schoolData.location.longitude?.toString() || "",
        boundaryPoints: schoolData.location.boundaryPoints || [],
      });
      console.log("GeneralSettings: Coordinates synced", schoolData.location);
    }
  }, [schoolData?.location]);

  const updateSchoolData = (newData) => {
    setSchoolData((prev) => ({ ...prev, ...newData }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      updateSchoolData({
        [parent]: { ...schoolData[parent], [child]: value },
      });
    } else {
      updateSchoolData({ [name]: value });
    }
  };

  // Enhanced geolocation options for maximum accuracy
  const geoOptions = {
    enableHighAccuracy: true,
    timeout: 30000, // Increased timeout
    maximumAge: 0, // Always get fresh reading
  };

  // Calculate average position from multiple readings
  const calculateAveragePosition = (positions) => {
    if (positions.length === 0) return null;

    const avgLat = positions.reduce((sum, pos) => sum + pos.latitude, 0) / positions.length;
    const avgLng = positions.reduce((sum, pos) => sum + pos.longitude, 0) / positions.length;
    const avgAccuracy = positions.reduce((sum, pos) => sum + pos.accuracy, 0) / positions.length;

    return {
      latitude: parseFloat(avgLat.toFixed(8)), // 8 decimal places for ~1cm accuracy
      longitude: parseFloat(avgLng.toFixed(8)),
      accuracy: avgAccuracy,
    };
  };

  // Start continuous location monitoring
  const startLocationCollection = (mode) => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation not supported by your browser");
      return;
    }

    setIsCollecting(true);
    setCollectionMode(mode);
    setReadings([]);
    setGeoError("");

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;

        setReadings((prev) => {
          const newReadings = [...prev, { latitude, longitude, accuracy, timestamp: Date.now() }];

          // Keep only last 10 readings for rolling average
          if (newReadings.length > 10) {
            newReadings.shift();
          }

          return newReadings;
        });

        setAccuracy(accuracy);
      },
      (error) => {
        setIsCollecting(false);
        setGeoError(getGeolocationError(error));
        if (watchIdRef.current) {
          navigator.geolocation.clearWatch(watchIdRef.current);
        }
      },
      geoOptions,
    );
  };

  // Stop location collection and save average
  const stopLocationCollection = () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    setIsCollecting(false);

    if (readings.length > 0) {
      const avgPosition = calculateAveragePosition(readings);

      if (collectionMode === "center") {
        setCoordinates((prev) => ({
          ...prev,
          centerLat: avgPosition.latitude.toString(),
          centerLng: avgPosition.longitude.toString(),
        }));
      } else if (collectionMode === "boundary") {
        setCoordinates((prev) => ({
          ...prev,
          boundaryPoints: [...prev.boundaryPoints, avgPosition],
        }));
      }
    }
  };

  // Calculate radius using multiple boundary points
  const calculateOptimalRadius = () => {
    const { centerLat, centerLng, boundaryPoints } = coordinates;

    if (!centerLat || !centerLng || boundaryPoints.length === 0) {
      setGeoError("Please set center coordinates and collect boundary points first");
      return null;
    }

    const centerPos = { lat: parseFloat(centerLat), lng: parseFloat(centerLng) };
    const distances = boundaryPoints.map((point) =>
      calculateDistance(centerPos.lat, centerPos.lng, point.latitude, point.longitude),
    );

    // Use maximum distance as radius to ensure all boundary points are included
    const radius = Math.max(...distances);

    return Math.ceil(radius); // Round up to ensure coverage
  };

  // Enhanced Haversine formula for accurate distance calculation
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371000; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  // Enhanced error handling
  const getGeolocationError = (error) => {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return "Location access denied. Please enable location services and refresh the page.";
      case error.POSITION_UNAVAILABLE:
        return "Location unavailable. Try moving to an area with better GPS signal.";
      case error.TIMEOUT:
        return "Location request timed out. Please try again.";
      default:
        return "Unknown location error occurred.";
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  // Remove boundary point
  const removeBoundaryPoint = (index) => {
    setCoordinates((prev) => ({
      ...prev,
      boundaryPoints: prev.boundaryPoints.filter((_, i) => i !== index),
    }));
  };

  const handleSaveChanges = async () => {
    const radius = calculateOptimalRadius();
    if (!radius) {
      setError("Please collect center coordinates and boundary points first");
      return;
    }

    setLoading(true);
    setError(null);

    const payload = {
      location: {
        latitude: parseFloat(coordinates.centerLat),
        longitude: parseFloat(coordinates.centerLng),
        radius: radius,
        boundaryPoints: coordinates.boundaryPoints,
      },
    };

    try {
      await userRequest.put(`/schools/${school._id}`, payload);
      console.log("Enhanced location saved successfully");
      setSuccess("Enhanced location settings saved successfully!");
      refreshSchool(); // Refresh school data to show updated location
    } catch (e) {
      console.log("Error saving enhanced location:", e);
      setError(e.response?.data?.message || "Error saving location settings");
    } finally {
      setLoading(false);
    }
  };

  // Show loading state if school data is not available
  if (!school) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading school data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Debug info */}
      {console.log(
        "Rendering GeneralSettings, success state:",
        success,
        "error state:",
        error,
        "geoError state:",
        geoError,
      )}

      {/* Notifications rendered at top level for mobile visibility */}
      {success && (
        <MobileFriendlyNotification
          type="success"
          message={success}
          onClose={() => setSuccess("")}
          autoClose={3000}
        />
      )}
      {error && (
        <MobileFriendlyNotification
          type="error"
          message={error}
          onClose={() => setError(null)}
          autoClose={5000}
        />
      )}
      {geoError && (
        <MobileFriendlyNotification
          type="error"
          message={geoError}
          onClose={() => setGeoError("")}
          autoClose={5000}
        />
      )}

      <div className="space-y-8" key={school?._id || "loading"}>
        {/* Logo Section */}
        <div>
          <h3 className="text-lg font-medium mb-2">Your Logo</h3>
          <p className="text-sm text-gray-500 mb-4">Add your school logo image</p>

          <div className="flex items-center">
            <div className="mr-6">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border">
                {school?.picture?.path ? (
                  <img
                    src={school.picture.path}
                    alt="School logo"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-gray-400 text-2xl">📚</span>
                )}
              </div>
            </div>

            <div className="space-x-4">
              <button
                type="button"
                onClick={() => setShowLogoModal(true)}
                style={{ backgroundColor: currentColor }}
                className="py-2 px-4 rounded-md text-white text-sm cursor-pointer inline-block"
                disabled={loading}
              >
                Update
              </button>

              {school?.picture?.path && (
                <button
                  type="button"
                  className="py-2 px-4 rounded-md text-red-500 text-sm border border-red-500"
                  onClick={async () => {
                    try {
                      setLoading(true);
                      await userRequest.patch(`/schools/${school._id}/change-picture`, null);
                      setSuccess("School logo removed successfully");
                      refreshSchool();
                    } catch (error) {
                      console.error(error);
                      setError("Failed to remove school logo. Please try again.");
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>

        <PhotoUploadModal
          isOpen={showLogoModal}
          onClose={() => setShowLogoModal(false)}
          onUploadSuccess={() => {
            setSuccess("School logo updated successfully");
            refreshSchool();
          }}
          uploadEndpoint={`/schools/${school?._id}/change-picture`}
          title="Upload School Logo"
          mediaLabel="Logo"
          fieldName="file"
          method="patch"
          outputFormat="png"
          maxFileSize={200 * 1024}
        />

        {/* School Information */}
        <div className="space-y-4">
          <FormInput
            label="School Name"
            name="school_name"
            value={schoolData?.school_name || ""}
            placeholder="Enter School Name"
            onChange={handleChange}
            required={true}
            disabled={true}
          />

          <FormInput
            label="Contact Email Address"
            name="school_email"
            value={schoolData?.school_email || ""}
            placeholder="Enter Email Address"
            onChange={handleChange}
            required={true}
            disabled={true}
          />

          <FormInput
            label="Contact Phone Number"
            name="phone_number"
            value={schoolData?.phone_number || ""}
            placeholder="Enter Phone Number"
            onChange={handleChange}
            required={true}
            disabled={true}
          />

          <FormInput
            label="Address"
            name="address.main.address_line"
            value={schoolData?.address?.main?.address_line || ""}
            placeholder="Enter School Address"
            onChange={handleChange}
            required={true}
            disabled={true}
          />

          <div className="lg:grid lg:grid-cols-2 lg:gap-x-2">
            <FormInput
              label="City"
              name="address.main.city"
              value={schoolData?.address?.main?.city || ""}
              placeholder="Enter City"
              onChange={handleChange}
              required={true}
              disabled={true}
            />

            <FormInput
              label="Postal Code"
              name="address.main.zip_code"
              value={schoolData?.address?.main?.zip_code || ""}
              placeholder="Enter Postal Code"
              onChange={handleChange}
              required={true}
              disabled={true}
            />
          </div>

          <label className="block">
            <span className="text-gray-700 dark:text-gray-200">State or Region</span>
            <select
              id="state"
              onChange={handleChange}
              name="address.main.state"
              value={schoolData?.address?.main?.state}
              className="bg-gray-100 border-gray-100 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
              required
              disabled={true}
            >
              <option value="KANO">KANO</option>
              <option value="LAGOS">LAGOS</option>
              <option value="KADUNA">KADUNA</option>
              <option value="KATSINA">KATSINA</option>
              <option value="OYO">OYO</option>
              <option value="RIVERS">RIVERS</option>
              <option value="BAUCHI">BAUCHI</option>
              <option value="JIGAWA">JIGAWA</option>
              <option value="BENUE">BENUE</option>
              <option value="ANAMBRA">ANAMBRA</option>
              <option value="BORNO">BORNO</option>
              <option value="DELTA">DELTA</option>
              <option value="NIGER">NIGER</option>
              <option value="IMO">IMO</option>
              <option value="AKWA_IBOM">AKWA_IBOM</option>
              <option value="OGUN">OGUN</option>
              <option value="SOKOTO">SOKOTO</option>
              <option value="ONDO">ONDO</option>
              <option value="OSUN">OSUN</option>
              <option value="KOGI">KOGI</option>
              <option value="ZAMFARA">ZAMFARA</option>
              <option value="ENUGU">ENUGU</option>
              <option value="KEBBI">KEBBI</option>
              <option value="EDO">EDO</option>
              <option value="PLATEAU">PLATEAU</option>
              <option value="ADAMAWA">ADAMAWA</option>
              <option value="CROSS_RIVER">CROSS_RIVER</option>
              <option value="ABIA">ABIA</option>
              <option value="EKITI">EKITI</option>
              <option value="KWARA">KWARA</option>
              <option value="GOMBE">GOMBE</option>
              <option value="YOBE">YOBE</option>
              <option value="TARABA">TARABA</option>
              <option value="EBONYI">EBONYI</option>
              <option value="NASARAWA">NASARAWA</option>
              <option value="BAYELSA">BAYELSA</option>
              <option value="FCT">FCT</option>
            </select>
          </label>
        </div>
        <hr className="my-4" />
        {/* Enhanced Geolocation Section */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium mb-2">📍 Enhanced School Geolocation</h3>

          {/* Current Saved Location Display */}
          {school?.location?.latitude && school?.location?.longitude && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-medium text-green-900 mb-3">Current Saved Location</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="bg-white p-3 rounded border">
                  <span className="text-gray-600">Latitude:</span>
                  <div className="font-mono text-green-800">{school.location.latitude}</div>
                </div>
                <div className="bg-white p-3 rounded border">
                  <span className="text-gray-600">Longitude:</span>
                  <div className="font-mono text-green-800">{school.location.longitude}</div>
                </div>
                <div className="bg-white p-3 rounded border">
                  <span className="text-gray-600">Radius:</span>
                  <div className="font-mono text-green-800">{school.location.radius}m</div>
                </div>
              </div>
              {school.location.boundaryPoints && school.location.boundaryPoints.length > 0 && (
                <div className="mt-3 text-sm text-green-700">
                  <span className="font-medium">Boundary Points:</span>{" "}
                  {school.location.boundaryPoints.length} points collected
                </div>
              )}
              {/*<div className="mt-3 text-xs text-gray-600">
              <span className="font-medium">Last Updated:</span> {new Date(school.updatedAt).toLocaleDateString()} at {new Date(school.updatedAt).toLocaleTimeString()}
            </div>*/}
              <div className="mt-3 flex flex-wrap gap-3">
                <a
                  href={`https://www.google.com/maps?q=${school.location.latitude},${school.location.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200 transition-colors"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  View on Google Maps
                </a>

                <a
                  href={`https://earth.google.com/web/search/${school.location.latitude},${school.location.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 transition-colors"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  View on Google Earth
                </a>
              </div>
            </div>
          )}

          {/*<div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">High-Precision Location Setup</h4>
          <p className="text-sm text-blue-700">
            For centimeter-level accuracy: Stand still for 30-60 seconds while collecting each point.
            Best results outdoors with clear sky view.
          </p>
        </div>*/}

          {/* Center Point Collection
      <div className="space-y-4">
          <h4 className="font-medium">1. Set School Center Point</h4>
          <div className="grid grid-cols-2 gap-4">
          <FormInput
              label="Center Latitude"
              value={coordinates.centerLat}
              onChange={(e) => setCoordinates(prev => ({ ...prev, centerLat: e.target.value }))}
              placeholder="Auto-filled when collecting"
          />
          <FormInput
              label="Center Longitude"
              value={coordinates.centerLng}
              onChange={(e) => setCoordinates(prev => ({ ...prev, centerLng: e.target.value }))}
              placeholder="Auto-filled when collecting"
            />
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={() => isCollecting ? stopLocationCollection() : startLocationCollection('center')}
              className="py-2 px-4 rounded-md text-white"
              style={{ backgroundColor: isCollecting && collectionMode === 'center' ? '#dc2626' : currentColor }}
            >
              {isCollecting && collectionMode === 'center' ? 'Stop Collection' : 'Collect Center Point'}
            </button>

            {isCollecting && collectionMode === 'center' && (
              <div className="text-sm text-gray-600">
                Readings: {readings.length} | Current Accuracy: ±{accuracy?.toFixed(1)}m
              </div>
            )}
          </div>
        </div>*/}

          {/* Boundary Points Collection
        <div className="space-y-4">
          <h4 className="font-medium">2. Collect Boundary Points</h4>
          <p className="text-sm text-gray-600">
            Walk to the school's perimeter and collect points from different locations (gates, corners, etc.)
          </p>

          <div className="flex items-center space-x-4">
            <button
              onClick={() => isCollecting ? stopLocationCollection() : startLocationCollection('boundary')}
              className="py-2 px-4 rounded-md text-white"
              style={{ backgroundColor: isCollecting && collectionMode === 'boundary' ? '#dc2626' : currentColor }}
            >
              {isCollecting && collectionMode === 'boundary' ? 'Save This Point' : 'Collect Boundary Point'}
          </button>

            {isCollecting && collectionMode === 'boundary' && (
              <div className="text-sm text-gray-600">
                Readings: {readings.length} | Accuracy: ±{accuracy?.toFixed(1)}m
        </div>
            )}
      </div>*/}

          {/* Display collected boundary points
          {coordinates.boundaryPoints.length > 0 && (
            <div className="mt-4">
              <h5 className="font-medium mb-2">Collected Points ({coordinates.boundaryPoints.length})</h5>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {coordinates.boundaryPoints.map((point, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <span className="text-sm">
                      Point {index + 1}: {point.latitude.toFixed(6)}, {point.longitude.toFixed(6)}
                      <span className="text-gray-500 ml-2">(±{point.accuracy?.toFixed(1)}m)</span>
                    </span>
                <button
                      onClick={() => removeBoundaryPoint(index)}
                      className="text-red-500 text-xs hover:text-red-700"
                    >
                      Remove
                </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div> */}

          {/* Results and Actions
        <div className="space-y-4">
          <h4 className="font-medium">3. Calculate and Save</h4>

          {coordinates.centerLat && coordinates.centerLng && coordinates.boundaryPoints.length > 0 && (
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-700">
                Calculated radius: {calculateOptimalRadius()}m based on {coordinates.boundaryPoints.length} boundary points
              </p>
            </div>
          )}

          <div className="flex space-x-4">
            <button
              onClick={handleSaveChanges}
              disabled={!coordinates.centerLat || !coordinates.centerLng || coordinates.boundaryPoints.length === 0}
              className="py-2 px-6 rounded-full text-white font-medium disabled:opacity-50"
              style={{ backgroundColor: currentColor }}
            >
              {loading ? 'Saving...' : 'Save Location Settings'}
            </button>

            <button
              onClick={() => setCoordinates({ centerLat: '', centerLng: '', boundaryPoints: [] })}
              className="py-2 px-6 rounded-full border border-gray-300 text-gray-700"
            >
              Reset All
            </button>
          </div>
        </div>*/}
        </div>

        <hr className="my-4" />

        {/* Term Settings */}
        <div>
          <h3 className="text-lg font-medium mb-4">Edit Term</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((termNumber) => (
              <TermSettings
                key={termNumber}
                termNumber={termNumber}
                termData={termData[termNumber]}
                onDateChange={handleTermDateChange}
              />
            ))}
          </div>
        </div>

        <hr className="my-6" />

        {/* Class Grouping & Shared Subjects Section */}
        <div>
          <h3 className="text-lg font-medium mb-2">Class Grouping</h3>
          <p className="text-sm text-gray-500 mb-4">
            For schools with more than one arm per level (e.g. JSS 1A, JSS 1B and JSS 1C). Group
            the arms under one class level (JSS 1) so you can set a subject, its teacher and its
            tests once for every arm, instead of repeating the work for each arm.
          </p>

          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-5">
            {/* Toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <span className="text-sm font-semibold text-slate-800">Turn on class grouping</span>
                <p className="text-xs text-slate-500 mt-0.5">
                  Each arm still keeps its own students and class teacher.
                </p>
                {classGroupingEnabled && (
                  <Link
                    to="/classes"
                    className="mt-1 inline-block text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                  >
                    Set up your class groups on the Classes page →
                  </Link>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span
                  className={`text-xs font-semibold ${classGroupingEnabled ? "text-indigo-600" : "text-slate-500"}`}
                >
                  {classGroupingEnabled ? "Enabled" : "Disabled"}
                </span>
                {classGroupingSaving && (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-500 border-t-transparent" />
                )}
                <button
                  type="button"
                  onClick={handleClassGroupingToggle}
                  disabled={classGroupingSaving}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60 ${
                    classGroupingEnabled ? "bg-indigo-600" : "bg-slate-300"
                  }`}
                  role="switch"
                  aria-checked={classGroupingEnabled}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                      classGroupingEnabled ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Subclass Label Config */}
            {classGroupingEnabled && (
              <div className="pt-4 border-t border-slate-200">
                <label
                  htmlFor="subclass_label"
                  className="block text-sm font-medium text-slate-700"
                >
                  What does your school call each arm?
                </label>
                <p className="text-xs text-slate-500 mt-0.5 mb-2">
                  This word is used across the dashboard, e.g. "Arm", "Stream" or "Section".
                </p>
                <form
                  onSubmit={handleSaveSubclassLabel}
                  className="flex items-center gap-3 max-w-md"
                >
                  <input
                    id="subclass_label"
                    type="text"
                    value={subclassLabelInput}
                    onChange={(e) => setSubclassLabelInput(e.target.value)}
                    placeholder="e.g. Arm, Stream, Section"
                    className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                  />
                  <button
                    type="submit"
                    disabled={subclassLabelSaving || !subclassLabelInput.trim()}
                    className="py-2 px-4 rounded-lg text-white text-sm font-medium bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition-colors flex-shrink-0"
                  >
                    {subclassLabelSaving ? "Saving..." : "Save Label"}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>

        <hr className="my-6" />

        {/* Admin Signature Section */}
        <div>
          <h3 className="text-lg font-medium mb-2 mt-8">Admin Signature</h3>
          <p className="text-sm text-gray-500 mb-4">
            Upload the signature image to be used on official documents and results.
          </p>
          <div className="flex items-center">
            <div className="mr-6">
              <div className="w-16 h-16 rounded bg-gray-100 flex items-center justify-center overflow-hidden border">
                {school?.admin_signature?.path ? (
                  <img
                    src={school.admin_signature.path}
                    alt="Admin signature preview"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <span className="text-gray-400 text-2xl">✍️</span>
                )}
              </div>
            </div>
            <div className="space-x-4">
              <button
                type="button"
                onClick={() => setShowSignatureModal(true)}
                style={{ backgroundColor: currentColor }}
                className="py-2 px-4 rounded-md text-white text-sm cursor-pointer inline-block"
              >
                Upload Signature
              </button>
              {school?.admin_signature?.path && (
                <button
                  type="button"
                  className="py-2 px-4 rounded-md text-red-500 text-sm border border-red-500"
                  onClick={async () => {
                    try {
                      setLoading(true);
                      await userRequest.patch(
                        `/schools/${school._id}/admin-signature`,
                        new FormData(),
                        {
                          headers: { "Content-Type": "multipart/form-data" },
                        },
                      );
                      setSuccess("Admin signature removed successfully");
                      refreshSchool();
                    } catch (error) {
                      setError("Failed to remove admin signature. Please try again.");
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>
        {/* End Admin Signature Section */}

        <PhotoUploadModal
          isOpen={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          onUploadSuccess={() => {
            setSuccess("Admin signature updated successfully");
            refreshSchool();
          }}
          uploadEndpoint={`/schools/${school?._id}/admin-signature`}
          title="Upload Admin Signature"
          mediaLabel="Signature"
          fieldName="signature"
          method="patch"
          aspectRatio={3}
          previewShape="rect"
          maxFileSize={1024 * 1024}
        />
      </div>
    </>
  );
};

export default GeneralSettings;
